import React, { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { Controller, useForm } from "react-hook-form";

export interface IpAddressValues {
  ipAddress: string;
  memo: string;
}

export function normalizeIpv4(value: string): string | undefined {
  if (value !== value.trim() || !/^[0-9]{1,3}(\.[0-9]{1,3}){3}$/.test(value)) return undefined;
  const parts = value.split(".").map(Number);
  return parts.every((x) => x <= 255) ? parts.join(".") : undefined;
}

interface Props {
  initial?: IpAddressValues;
  otherAddresses: string[];
  onSubmit: (value: IpAddressValues) => void;
  onClose: () => void;
}

export const IpAddressDialog: React.VFC<Props> = ({
  initial,
  otherAddresses,
  onSubmit,
  onClose,
}) => {
  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<IpAddressValues>({
    mode: "all",
    defaultValues: { ipAddress: "", memo: "" },
  });
  useEffect(() => {
    reset(initial ?? { ipAddress: "", memo: "" });
  }, [initial, reset]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="ip-dialog-title"
    >
      <DialogTitle id="ip-dialog-title">
        IPアドレスの{initial ? "編集" : "追加"}
      </DialogTitle>
      <form
        onSubmit={handleSubmit((value) =>
          onSubmit({ ...value, ipAddress: normalizeIpv4(value.ipAddress)! })
        )}
      >
        <DialogContent>
          <Controller
            name="ipAddress"
            control={control}
            rules={{
              required: "IPアドレスを入力してください。",
              validate: (value) => {
                const address = normalizeIpv4(value);
                if (!address) return "正しいIPアドレスを入力してください";
                return (
                  !otherAddresses.some((x) => normalizeIpv4(x) === address) ||
                  `${value}は既に登録されています。`
                );
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                id="restriction-ip-address"
                label="IPアドレス"
                variant="outlined"
                autoFocus
                fullWidth
                margin="dense"
                error={!!errors.ipAddress}
                helperText={errors.ipAddress?.message}
              />
            )}
          />
          <Controller
            name="memo"
            control={control}
            rules={{
              maxLength: {
                value: 50,
                message: "備考は50文字以内で入力してください。",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                id="restriction-ip-memo"
                label="備考"
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.memo}
                helperText={errors.memo?.message}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" type="submit" disabled={!isValid}>
            {initial ? "更新" : "追加"}
          </Button>
          <Button onClick={onClose}>キャンセル</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
